import { LoadingSpinner } from '@ecomm/checkout-icons'
import type { CheckoutFormValuesType } from '@ecomm/checkout/shipping-schema'
import {
  useCartSetShippingMethod,
  useCartShippingMethodId,
  useShippingMethods,
  useShippingMethodsLoading
} from '@ecomm/data-cart'
import { useLocale } from '@ecomm/data-hooks'
import { useFormikContext } from 'formik'
import { useEffect } from 'react'

import { FormSection } from '../FormSection'
import { Option } from './Option'
import { useDisplayDisclaimer } from './useDisplayDisclaimer'

export function ShippingOptions() {
  const locale = useLocale()

  const methods = useShippingMethods()
  const isLoading = useShippingMethodsLoading()

  const displayDisclaimer = useDisplayDisclaimer()

  const cartMethodId = useCartShippingMethodId()
  const setShippingMethod = useCartSetShippingMethod()

  const { values: formik, setFieldValue } =
    useFormikContext<CheckoutFormValuesType>()

  // If the shipping method on cart changes, sync it to the formik value if they differ to prevent drift
  useEffect(() => {
    cartMethodId &&
      cartMethodId !== formik.shippingOption &&
      setFieldValue('shippingOption', cartMethodId)
    // Only run if the cart shipping method has changed
  }, [cartMethodId])

  // If a user changes the form value, set the shipping method on cart
  useEffect(() => {
    formik.shippingOption &&
      formik.shippingOption !== cartMethodId &&
      setShippingMethod(formik.shippingOption)
    // Only run if the formik shipping method has changed
  }, [formik.shippingOption])

  const isUserAddressSet =
    !!formik.postalCode &&
    !!formik.streetName &&
    !!formik.country &&
    !!formik.city

  return (
    <div data-component="ShippingOption">
      <FormSection title="Shipping options">
        {!isUserAddressSet ? (
          <div className="rounded-base bg-neutral-light-50 w-full p-4">
            Enter your address for shipping options
          </div>
        ) : (
          <fieldset className="rounded-base bg-neutral-light-50 flex w-full flex-col gap-4 p-4 border-0 mx-0">
            <legend className="sr-only">Shipping options</legend>
            <h3 className="m-0 text-lg font-bold">
              Shipping options for {formik.streetName}, {formik.city},{' '}
              {formik.state} {formik.postalCode}
            </h3>
            {locale === 'en-GB' ? (
              <h3 className="text-base font-normal md:text-lg">
                Shipping quotes are generated automatically when you enter your
                address. Free delivery on all orders over £50.
              </h3>
            ) : null}
            <div className="flex flex-col w-full justify-center">
              {isLoading ? (
                <LoadingSpinner size={96} />
              ) : displayDisclaimer ? (
                <>
                  <h3 className="text-xs font-bold leading-tight">
                    Please Note
                  </h3>
                  <p
                    className="text-base font-light md:text-lg"
                    data-testid="shipping-battery-disclaimer"
                  >
                    If you are shipping to AK, GU, HI, PR or VI and have an
                    Outdoor Camera Battery in cart, you must first remove it due
                    to shipping restrictions.
                  </p>
                </>
              ) : (
                methods.map((method, idx) => <Option data={method} key={idx} />)
              )}
            </div>
          </fieldset>
        )}
      </FormSection>
    </div>
  )
}
